import styled from '@emotion/styled';

import { keepValueWithinRange } from '@toolbox/utils';
import { useUncontrolledProp } from '@toolbox/hooks';
import { Histogram } from '../Histogram';
import { Input } from '../Input';
import { maxNumberValue } from '../Histogram/utils';
import { Flex, FlexItem } from '../Flex';
import { RangeSlider } from '../RangeSlider';

import type { FocusEvent, ReactNode, ChangeEvent } from 'react';

const RangeSliderWrapper = styled.div`
  ${FlexItem} + ${FlexItem} {
    margin-left: ${({ theme }) => theme.spacing(8)};
  }
`;

type RangeSliderWithHistogramProps = {
  data: number[];
  valueSuffix?: ReactNode;
  histogramHeight?: number;
  className?: string;
  minLabel?: string;
  maxLabel?: string;
  value?: [number, number];
  step?: number;
  onChange?: (value: number[]) => void;
};

export const RangeSliderWithHistogram = ({
  data,
  valueSuffix,
  histogramHeight,
  className,
  minLabel,
  maxLabel,
  step,
  ...props
}: RangeSliderWithHistogramProps): JSX.Element => {
  let maxValueFromData = maxNumberValue(data);

  const [value, setValue] = useUncontrolledProp<[number, number]>(
    props.value,
    [0, maxValueFromData],
    props.onChange,
  );
  const [min, max] = value;

  if (!maxValueFromData || maxValueFromData <= 0) {
    maxValueFromData = max;
  }

  const handleSliderChange = (newValue: number[]): void => {
    setValue(newValue as [number, number]);
  };

  const handleMinInputChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const newMin = keepValueWithinRange(parseInt(event.target.value, 10), maxValueFromData, 0);
    handleSliderChange([newMin, max]);
  };

  const handleMaxInputChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const newMax = keepValueWithinRange(parseInt(event.target.value, 10), maxValueFromData, 0);
    handleSliderChange([min, newMax]);
  };

  const handleMinInputOnBlur = (e: FocusEvent<HTMLInputElement>): void => {
    e.preventDefault();
    if (isNaN(min)) setValue([0, max]);
    if (max < min) setValue([min, min]);
  };

  const handleMaxInputOnBlur = (e: FocusEvent<HTMLInputElement>): void => {
    e.preventDefault();
    if (isNaN(max)) setValue([min, maxValueFromData]);
    if (max < min) setValue([min, min]);
  };

  const handleFocus = (e: FocusEvent<HTMLInputElement>): void => e.target.select();
  const assignInputValue = (value: number): number | string => (!isNaN(value) ? value : '');

  return (
    <RangeSliderWrapper className={className}>
      {data && data.length > 0 && (
        <Histogram highlightStart={min} highlightEnd={max} data={data} height={histogramHeight} />
      )}
      <RangeSlider value={value} max={maxValueFromData} step={step} onChange={handleSliderChange} />
      <Flex justifyContent="space-between">
        <FlexItem>
          <Input
            label={minLabel}
            type="number"
            value={assignInputValue(min)}
            onChange={handleMinInputChange}
            onFocus={handleFocus}
            onBlur={handleMinInputOnBlur}
            suffix={valueSuffix}
          />
        </FlexItem>
        <FlexItem>
          <Input
            label={maxLabel}
            type="number"
            value={assignInputValue(max)}
            onChange={handleMaxInputChange}
            onFocus={handleFocus}
            onBlur={handleMaxInputOnBlur}
            suffix={valueSuffix}
          />
        </FlexItem>
      </Flex>
    </RangeSliderWrapper>
  );
};
